import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Siren } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { AlertList } from "@/components/kpi/AlertList";

const POLL_MS = 5 * 60 * 1000;

/** Management alerts in the top bar: a red count of open alerts (RED first), acknowledge-with-note in place. */
export function KpiAlertBell() {
  const btnRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ left: 0, top: 0 });
  const [alerts, setAlerts] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/kpis/alerts", { params: { status: "OPEN" } });
      setAlerts(Array.isArray(data) ? data : []);
    } catch { /* the bell must never break the page */ }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, POLL_MS); return () => clearInterval(t); }, [load]);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (!btnRef.current?.contains(e.target) && !document.querySelector("[data-kpi-bell-portal]")?.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", close);
    window.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", close); window.removeEventListener("keydown", esc); };
  }, [open]);

  const toggle = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ left: Math.max(8, Math.min(r.right - 480, window.innerWidth - 496)), top: r.bottom + 6 });
      load();
    }
    setOpen((o) => !o);
  };
  const red = alerts.filter((a) => a.level === "RED").length;

  return (
    <>
      <button ref={btnRef} type="button" onClick={toggle} aria-expanded={open} data-testid="kpi-alert-bell"
        title="Management alerts" aria-label={alerts.length ? `Management alerts, ${alerts.length} open, ${red} red` : "Management alerts"}
        className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-transparent text-[#E6EBF5] transition-colors hover:bg-white/10 hover:text-white">
        <Siren className="h-5 w-5" />
        {alerts.length > 0 && (
          <span data-testid="kpi-alert-badge" className={cn("absolute -right-1 -top-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full px-1 text-[9px] font-bold leading-none text-white", red ? "bg-red-600" : "bg-amber-500")}>
            {alerts.length > 99 ? "99+" : alerts.length}
          </span>
        )}
      </button>
      {open && createPortal(
        <div data-kpi-bell-portal role="dialog" aria-label="Management alerts" data-testid="kpi-alert-panel"
          style={{ position: "fixed", left: pos.left, top: pos.top, zIndex: 9999, width: 480, maxWidth: "calc(100vw - 16px)" }}
          className="flex max-h-[75vh] flex-col overflow-hidden rounded-2xl border border-border bg-popover text-popover-foreground shadow-2xl">
          <div className="flex items-center justify-between border-b px-4 py-3">
            <div>
              <div className="text-sm font-semibold">Management alerts</div>
              <div className="text-xs text-muted-foreground">{alerts.length ? `${alerts.length} open · ${red} red` : "Nothing open"}</div>
            </div>
            <Link to="/performance?view=alerts" onClick={() => setOpen(false)} className="text-xs font-medium text-[#910029] hover:underline dark:text-rose-300">All alerts</Link>
          </div>
          <div className="overflow-y-auto">
            <AlertList alerts={alerts} compact onChanged={load} onNavigate={() => setOpen(false)} />
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
